<script lang="ts">
  	import { defaultActivityChartSeriesVisibility, type ActivityAxisType, type XAxisData, type SeriesVisibility, type MapControllerInterface, iconBicycle, noopMapController, iconDiamond, type AfterChartZoom, defaultMapControllerInterface} from "@/Common/constants";
  	import type { MyFitnessStores } from "@/MyFitness/stores";
	import { activityChartPalette, createChartController, createEmptyChartController, getXertAm5, type ActivityChartMpaData, type am5Root, type am5xyChart, type ChartController, type ProcessedDataPoint, type StravaSegmentController } from "@/Util/XertAm5";
  	import type { Legend } from "@amcharts/amcharts5";
  	import { getContext, onMount } from "svelte";
	import * as RadioButtonGroup from "$lib/components/ui/radio-button-group/index.js";
    import type { LineSeries } from "@amcharts/amcharts5/xy";
    import { cn } from "@/lib/utils";
    import { writable } from "svelte/store";
    import ScrollArea from "@/lib/components/ui/scroll-area/scroll-area.svelte";

    export let mpaData: ActivityChartMpaData
    export let startingAxisType: ActivityAxisType = "time"
    export let showLegend = true
    export let hideYAxis = false
    export let showScrollbar = true
    export let allowExport = false
    export let seriesVisibility: SeriesVisibility = {...defaultActivityChartSeriesVisibility}
    /** Adds the interval length to the power tooltip. Only meaningful for workout data, which carries it. */
    export let showIntervalDuration = false
    export let map: MapControllerInterface = {
        controller: noopMapController,
        group: null,
    }
    map = {...defaultMapControllerInterface, ...map}
    let className: string = undefined
    export {className as class}
    export let afterZoom: AfterChartZoom = () => {}

    // For bind
    export let chartController: ChartController<StravaSegmentController<ProcessedDataPoint>> = createEmptyChartController<StravaSegmentController<ProcessedDataPoint>>()

	const myFitness = getContext<MyFitnessStores>('myFitness')
	const { userParams } = myFitness

    const { 
		am5, am5xy, am5plugins_exporting, MyTheme, MyLineSeries, createCursor, createMyDateAxis, createPowerAxis, createSpeedAxis, fastGroupData, 
		createAltitudeAxisSeries, createCadenceAxisSeries, createDifficultyAxisSeries, createHeartRateAxisSeries, hideSeriesAxisWhenDeselected,
		createPowerFillSeries, createMPASeries, createPeakPowerItem, createThresholdPowerItem, createStravaSegmentAnalysisController, chartReady
	} = getXertAm5()
    am5.addLicense('AM5C87942260');
	
	let dataDist: ProcessedDataPoint[]
    let chartElement: HTMLElement
	let legendElement: HTMLElement
    let legendContainer: HTMLElement
    let root: am5Root
    let chart: am5xyChart
    let axisType = writable<ActivityAxisType>(startingAxisType)
    let stravaSegmentController: StravaSegmentController<ProcessedDataPoint> = null
    
    let validateData: (axisType: ActivityAxisType) => void
	let switchAxisType: (axisType: ActivityAxisType) => void
    function getChartData(axisType: ActivityAxisType) {
        return axisType === "time" ? mpaData.data : dataDist
    }
    let switchingAxisType = false

    // If mpaData is changed revalidate the data.
    const revalidateData = () => {if(validateData) validateData($axisType)}
    $: if(mpaData) revalidateData()

    onMount(() => {

        seriesVisibility = {
            ...defaultActivityChartSeriesVisibility,
            ...seriesVisibility,
        }

		// TODO: This is copied right now between charts. Maybe create a standardXertChart() ??
      	root = am5.Root.new(chartElement);
        root.setThemes([MyTheme.new(root)]);

        chart = root.container.children.push(am5xy.XYChart.new(root, {
            paddingLeft: hideYAxis ? 15 : 0,
            paddingTop: -8,
            layout: root.verticalLayout,
        }));

        chart.topPlotContainer.children.removeValue(chart.zoomOutButton);
        let buttonsContainer = chart.topPlotContainer.children.push(am5.Container.new(root, {
            layout: root.horizontalLayout,
            width: am5.p100,
            paddingRight: 10,
            //x: am5.p100
        }));

        buttonsContainer.children.push(am5.Container.new(root, {
            width: am5.p100,
        }));
        let zoomOutButton = buttonsContainer.children.push(chart.zoomOutButton);
        // Hack because am5 has a bug where zoomOut doesn't hide the button like it should
        //@ts-expect-error
        chart._handleZoomOut = ()=>{
            // Only compare x-axis for zoom button.
            if ((dateAxis.get('start') == 0) && (dateAxis.get('end') == 1)) {
                zoomOutButton.hide();
            } else {
                zoomOutButton.show();
            }
        };

        const {
            dateAxis,
            toggleX,
            setMainDataSeries,
            setValidateData,
            createScrollbar,
        } = createMyDateAxis({
            root,
            chart,
            startingAxisType,
            distanceUnits: $userParams.activityUnits['dist'],
            handleRangeChange: (selectedMin, selectedMax, _axisType) => {
                map.controller.setCenterFromData(selectedMin, selectedMax, getChartData(_axisType));
                afterZoom(selectedMin, selectedMax, getChartData(_axisType), _axisType);
            },
        });
        const {sbDateAxis, sbFillSeries} = createScrollbar({root, chart, visible: showScrollbar})
        
		const powerAxis = createPowerAxis({root, chart, forceHidden: hideYAxis || seriesVisibility.power === 'off'})
        const speedAxis = createSpeedAxis({root, chart, unit: $userParams.activityUnits['spd'], startVisible: seriesVisibility.speed === "startVisible", forceHidden: hideYAxis})
        const {altitudeSeries, altitudeAxis} = createAltitudeAxisSeries({root, chart, dateAxis, startingAxisType, startVisible: seriesVisibility.altitude === "startVisible", unit: $userParams.activityUnits["alt"], forceHidden: hideYAxis})
		const {cadenceSeries, cadenceAxis} = createCadenceAxisSeries({root, chart, dateAxis, startingAxisType, startVisible: seriesVisibility.cadence === "startVisible", forceHidden: hideYAxis})
		const {difficultySeries, difficultyAxis} = createDifficultyAxisSeries({root, chart, dateAxis, startingAxisType, startVisible: seriesVisibility.difficulty === "startVisible", forceHidden: hideYAxis})
		const {heartRateSeries, heartRateAxis} = createHeartRateAxisSeries({root, chart, dateAxis, startingAxisType, startVisible: seriesVisibility.heartRate === "startVisible", forceHidden: hideYAxis})
		const {powerFillSeries, resetGradientStops} = createPowerFillSeries({root, chart, dateAxis, startingAxisType, axis: powerAxis, signature: mpaData.signature, startVisible: seriesVisibility.power === 'startVisible', showIntervalDuration})
        const MPASeries = createMPASeries({root, chart, dateAxis, startingAxisType, startVisible: seriesVisibility.mpa === "startVisible", axis: powerAxis})
        const peakPowerDataItem = createPeakPowerItem(powerAxis)
        const thresholdPowerDataItem = createThresholdPowerItem(powerAxis)

		// TODO: figure out speed inverted stuff
		const speedSeries = chart.series.push(am5xy.LineSeries.new(root, {
            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'spd',
            name: 'Speed',
            stroke: am5.color(activityChartPalette.speed),
            yAxis: speedAxis,
            xAxis: dateAxis,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
				// labelText: '{name}: [bold]{' + (isSpeedInverted  ? 'valueY.formatDuration("m:ss")' : 'valueY.formatNumber("#.")') + '}' + $userParams.activityUnits.spd + '[/]',
				labelText: '{name}: [bold]{' + (false  ? 'valueY.formatDuration("m:ss")' : 'valueY.formatNumber("#.")') + '}' + $userParams.activityUnits.spd + '[/]',
            }),
            // visible: (noPower || isSpeedInverted) && seriesVisibility.speed,
			visible: seriesVisibility.speed === "startVisible",
            connect: false,
            tooltipY: 0,
            tooltipX: 0,
        }));
        speedSeries.strokes.template.setAll({
            strokeWidth: 2.0,
        });
        speedSeries.on('visible', function(visible) {
            if (visible)
                speedAxis.show();
            else
                speedAxis.hide();
            speedSeries.get('tooltip')?.set('forceHidden', !visible);
        });
        if (seriesVisibility.speed !== "startVisible") speedSeries.get('tooltip')?.set('forceHidden', true);

		const overageEffortsSeries = chart.series.push(am5xy.LineSeries.new(root, {
            connect: false,
            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'overage_power',

            name: 'Efforts Over MPA',

            stroke: am5.color('#FFFF00'),

            yAxis: powerAxis,
            xAxis: dateAxis,

        }));
        overageEffortsSeries.strokes.template.setAll({
            strokeWidth: 5
        });

		const bestEffortsSeries = chart.series.push(MyLineSeries.new(root, {
			valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
			valueYField: 'power',
			name: 'Best Effort',
			yAxis: powerAxis,
			xAxis: dateAxis,
			tooltip: am5.Tooltip.new(root, {
				labelText: '{name}',
			}),
		}));

        MPASeries.on('visible', function(visible) {
            if (visible) {
                bestEffortsSeries.show();
                overageEffortsSeries.show();
            } else {
                bestEffortsSeries.hide();
                overageEffortsSeries.hide();
            }
        });

        const tgtSeries = chart.series.push(am5xy.LineSeries.new(root, {
            connect: true,
            valueXField: startingAxisType === "time" ? 'unix_time' : "mm",
            valueYField: 'tgt',
            name: 'Target Power',
            stroke: am5.color(activityChartPalette.targetPower), // TODO: MPA color gradient?
            yAxis: powerAxis,
            xAxis: dateAxis,
            tooltip: am5.Tooltip.new(root, {
                pointerOrientation: 'horizontal',
                labelText: '{name}: [bold]{valueY.formatNumber("#.")} W[/]'
            }),
            visible: seriesVisibility.targetPower === "startVisible",
        }));
        tgtSeries.strokes.template.setAll({
            strokeWidth: 4.0,
            strokeDasharray: [15, 15],
            strokeOpacity: 0.8,
            lineJoin: 'round',
        });
        tgtSeries.on('visible', function(visible) {
            tgtSeries.get('tooltip')?.set('forceHidden', !visible);
        });
        if (seriesVisibility.targetPower !== "startVisible") tgtSeries.get('tooltip')?.set('forceHidden', true);
        
        let legend: Legend | null
        if (showLegend) {
            const legendRoot = am5.Root.new(legendElement);
            legendRoot.setThemes([am5xy.DefaultTheme.new(legendRoot)]);

            const bottomContainer = legendRoot.container.children.push(am5.Container.new(legendRoot, {
                layout: legendRoot.horizontalLayout,
                width: am5.p100,
            }));
            legend = bottomContainer.children.push(am5.Legend.new(legendRoot, {
                centerY: am5.percent(50),
                y: am5.percent(50),
            }));
            legend.valueLabels.template.set("forceHidden", true);
            legend.itemContainers.template.setAll({
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 36,
                paddingTop: 0,
            })
            bottomContainer.events.on("boundschanged", function() {
                legendElement.style.height = bottomContainer.height() + "px";
            });

            // ? This was originally in validate data but I moved it out and it seems to work fine. Keep an eye on it.
            const series: LineSeries[] = []
            if (seriesVisibility.power !== "off") series.push(powerFillSeries)
            if (seriesVisibility.mpa !== "off") series.push(MPASeries)
            if (seriesVisibility.cadence !== "off") series.push(cadenceSeries) 
            if (seriesVisibility.altitude !== "off") series.push(altitudeSeries) 
            if (seriesVisibility.difficulty !== "off") series.push(difficultySeries) 
            if (seriesVisibility.speed !== "off") series.push(speedSeries)
            if (seriesVisibility.heartRate !== "off") series.push(heartRateSeries) 
            if (seriesVisibility.targetPower !== "off") series.push(tgtSeries) 
            legend.data.setAll(series)
        }

		setMainDataSeries(powerFillSeries)

        stravaSegmentController = createStravaSegmentAnalysisController<ProcessedDataPoint>(root, chart, legend, dateAxis, powerAxis, mpaData.data, startingAxisType, map)    

        map.controller.addMarker({
            id: "user",
            lat: mpaData.data[0].lat,
            lng: mpaData.data[0].lng,
            image: iconBicycle, // TODO: Make this based on sport
            group: map.group,
            size: {
                width: 20,
                height: 20,
            },
        });
        map.controller.addMarker({
            id: "bestEffort",
            lat: mpaData.data[0].lat,
            lng: mpaData.data[0].lng,
            image: iconDiamond,
            group: map.group,
        }); 

        createCursor({root, chart, handleChange: (pos) => {
            let position = dateAxis.toAxisPosition(pos); // TODO: Pretty sure that this just returns the same number as pos  

            const dataContext = dateAxis.getSeriesItem(powerFillSeries, position)?.dataContext
            // Sometimes dataContext can be null (seems to happen when mousing out of the chart)
            if(dataContext) map.controller.setMarkerPosition("user", dataContext.lat, dataContext.lng, map.group);
            stravaSegmentController.updateMarkers(position);
        }})
        
		validateData = function(axisType: ActivityAxisType) {

			resetGradientStops();
			({dataDist} = fastGroupData(
                [
                    powerFillSeries, MPASeries, tgtSeries, overageEffortsSeries, 
                    cadenceSeries, heartRateSeries, speedSeries, difficultySeries, altitudeSeries, sbFillSeries,
                ], 
                mpaData.data, axisType, [dateAxis, sbDateAxis]
            ));

			const bestEffort = mpaData.min;
            bestEffortsSeries.bullets.clear();
            if (bestEffort != null) {
                if ( bestEffort.bt_overage_power > 0) {
                    bestEffortsSeries.set('name',  'Breakthrough');
                    bestEffortsSeries.bullets.push((root)=>am5.Bullet.new(root, {
                        sprite: am5.Rectangle.new(root, {
                            centerX: am5.p50,
                            centerY: am5.p50,
                            width: 10,
                            height: 10,
                            rotation: 45,
                            fill: am5.color('#00FF00'),
                            stroke: am5.color('#00FF00'),
                            strokeWidth: 1.0,
                        })
                    }));
                } else {
                    bestEffortsSeries.set('name',  'Best Effort');
                    bestEffortsSeries.bullets.push((root)=>am5.Bullet.new(root, {
                        sprite: am5.Circle.new(root, {
                            radius: 5,
                            fill: am5.color('#00FF00'),
                            stroke: am5.color('#00FF00'),
                            strokeWidth: 1.0,
                        })
                    }));
                }
			}
            if (bestEffort != null) {
                bestEffortsSeries.setDataUngrouped([bestEffort])
                map.controller.setMarkerPosition("bestEffort", bestEffort.lat, bestEffort.lng, map.group);
                map.controller.updateMarkerLabel("bestEffort", `Best Effort ${bestEffort.power}W`, map.group);
            }
      
			thresholdPowerDataItem.set('value', mpaData.signature.ftp)
        	peakPowerDataItem.set('value', mpaData.signature.pp)

            stravaSegmentController.switchAxis(axisType, getChartData(axisType));

		}
        validateData(startingAxisType)
		setValidateData(validateData)

        hideSeriesAxisWhenDeselected({root, chart})

        if(allowExport) {
            let exporting = am5plugins_exporting.Exporting.new(root, {
                menu: am5plugins_exporting.ExportingMenu.new(root, {}),
    
                // For some reason dataSource can't be empty initially, otherwise CSV export option missing.
                dataSource: [{dummy: 123}],
    
                dataFields: {
                    unix_time: 'Timestamp (ms)',
                    time: 'Time (s)',
                    power: '5s Average Power (W)',
                    p_raw: 'Power (W)',
                    tgt: 'Target Power (W)',
                    mpa: 'MPA (W)',
                    xds: 'Difficulty',
                    lat: 'Latitude (degrees)',
                    lng: 'Longitude (degrees)',
                    spd: 'Speed (km / hour)',
                    hr: 'Heart Rate (bpm)',
                    cad: 'Cadence (rpm)',
                    alt: 'Altitude (m)',
                    dist: 'Distance (km)',
                    tgf: 'Fat Burned (g)',
                    tgc: 'Carbs Burned (g)',
                }
    
            });
            exporting.adapters.add('dataSource', () => {
                let data: (ProcessedDataPoint & {time?: string})[] = mpaData.data
                if (!data) return []
    
                const t0 = data[0].unix_time
                for (let i = 0; i < data.length; i++) {
                    data[i].time = ((data[i].unix_time - t0)/1000).toFixed(0)
                }
                return data
            })
        }

        // The event handler needs to be separated from the function to allow for the dom to update correctly.
        async function _switchAxisType(_axisType: ActivityAxisType) {
            if(_axisType === $axisType) return;
            switchingAxisType = true;
            $axisType = await toggleX(_axisType === "time" ? dataDist : mpaData.data)
            switchingAxisType = false
        }
        switchAxisType  = function(axisType: ActivityAxisType) {
            switchingAxisType = true;
            setTimeout(() => _switchAxisType(axisType))
        }

        // No power change atm
        chartController = createChartController<StravaSegmentController<ProcessedDataPoint>>(stravaSegmentController, axisType, _switchAxisType, () => {}, chartReady(root))

        if (showLegend) {
            dateAxis.events.on('boundschanged', (event)=>{
                let w = dateAxis.width();
                if (w > 0) {
                    dateAxis.set('groupCount', w);
                    sbDateAxis.set('groupCount', w);
                    legendContainer.style.width = w + "px";

                }
            });
        }
    })

</script>

<div class={cn(
    "relative",
    switchingAxisType && "pointer-events-none"
)}>

    {#if switchingAxisType}
        <div class="absolute inset-0 z-10">
            <i class="fa-solid fa-spinner animate-spin text-2xl text-muted-foreground"/>
        </div>
    {/if}

    <div bind:this={chartElement} class={cn("h-96", className)}/>
    {#if showLegend}
    <div class={cn("space-y-3 w-full", hideYAxis ? 'ml-[15px]' : 'ml-[3.25rem]')} class:hidden={!showLegend}>
        <div bind:this={legendContainer} class="border border-border rounded px-4 py-2"> 
            <div bind:this={legendElement} />
        </div>
        
        <div class="flex gap-4 items-center">
            <!-- TODO: Maybe make the axis switch buttons a component since it's the same for GroupActivityChart -->
            <RadioButtonGroup.Root variant="outline" grouped value={$axisType} 
                onValueChange={switchAxisType}
            >
                <RadioButtonGroup.Button value="time">
                    Time
                </RadioButtonGroup.Button>
                <RadioButtonGroup.Button value="distance">
                    Distance
                </RadioButtonGroup.Button>
            </RadioButtonGroup.Root>
            <slot name="buttons" />
        </div>
    </div>
    {/if}
</div>

